import { motion } from 'motion/react';
import { Calendar, MessageCircle, Mail, Handshake } from 'lucide-react';
import { useTranslation } from '../i18n';

const steps = [
  {
    number: '01',
    icon: Calendar,
    titleKey: 'workflow.steps.submit.title',
    subtitleKey: 'workflow.steps.submit.subtitle',
    descriptionKey: 'workflow.steps.submit.description',
  },
  {
    number: '02',
    icon: MessageCircle,
    titleKey: 'workflow.steps.consultant.title',
    subtitleKey: 'workflow.steps.consultant.subtitle',
    descriptionKey: 'workflow.steps.consultant.description',
  },
  {
    number: '03',
    icon: Mail,
    titleKey: 'workflow.steps.payment.title',
    subtitleKey: 'workflow.steps.payment.subtitle',
    descriptionKey: 'workflow.steps.payment.description',
  },
  {
    number: '04',
    icon: Handshake,
    titleKey: 'workflow.steps.support.title',
    subtitleKey: 'workflow.steps.support.subtitle',
    descriptionKey: 'workflow.steps.support.description',
  },
];

export function Workflow() {
  const { t } = useTranslation();
  return (
    <section id="workflow" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-[#0A1A33] mb-4 text-[36px]">{t('workflow.section.title')}</h2>
          <p className="text-gray-600">{t('workflow.section.subtitle')}</p>
        </motion.div>

        <div className="relative">
          {/* Connection Line */}
          <div className="hidden lg:block absolute top-24 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#C6A667]/30 to-transparent" 
               style={{ width: '85%', marginLeft: '7.5%' }} 
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -8 }}
                className="relative group"
              >
                <div className="h-full bg-white rounded-2xl border-2 border-gray-100 p-8 transition-all duration-300 group-hover:border-[#C6A667]/40 group-hover:shadow-xl">
                  {/* Number Badge - Top Left */}
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-[#C6A667] to-[#C6A667]/80 mb-6 group-hover:scale-110 transition-transform duration-300">
                    <span className="text-white font-bold">{step.number}</span>
                  </div>

                  {/* Icon section removed */}

                  {/* Content */}
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#C6A667] mb-2 font-semibold">{t(step.subtitleKey)}</p>
                    <h4 className="text-[#0A1A33] mb-3 font-bold">{t(step.titleKey)}</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">{t(step.descriptionKey)}</p>
                  </div>

                  {/* Bottom accent line */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#C6A667] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-b-2xl" />
                </div>

                {/* Arrow connector for desktop */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-16 -right-4 z-10">
                    <div className="w-8 h-8 rounded-full bg-white border-2 border-[#C6A667]/30 flex items-center justify-center">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M3 8h10M10 5l3 3-3 3" stroke="#C6A667" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
